// @ts-nocheck

import React, { useEffect } from 'react';
import type { ElementRef } from 'react';
import { useDispatch } from 'react-redux';

import { setShowWinamaxTv } from '../../actions/user';
import { Target } from '../../client';
import { StreamModes } from '../../constants/picture-in-picture';
import usePictureInPicture from '../../hooks/usePictureInPicture';
import usePrevious from '../../hooks/usePrevious';
import { PiPWrap } from '../../styled-components/shared';

type Props = {
  matchId: number;
  isExpand: boolean;
  isTvItemSelected: boolean;
  setActiveItem: (...params: unknown) => unknown;
  tvItemId: string;
  offsetTop?: number;
  isSticky?: boolean;
  placeholderRef: ElementRef<unknown>;
};

const PiPMatchToolbarController = ({
  matchId,
  isExpand,
  setActiveItem,
  isTvItemSelected,
  tvItemId,
  isSticky = false,
  offsetTop = 0,
  placeholderRef,
}: Props) => {
  const {
    askRestore,
    askPip,
    askWtv,
    closeStream,
    floatingVideoRef,
    isMatchRouteRelativeToStream,
    isPipCustomActive,
    isPipEnabled,
    isPipNativeActive,
    isSameMatchRouteAndPipNativeActive,
    setAskRestore,
    setAskPip,
    setAskWtv,
    setPlaceholderRef,
    setStickyOffsetTop,
    startStream,
    type,
  } = usePictureInPicture();

  // set a ref to the placeholder which comes from the Picture in Picture provider
  useEffect(() => {
    if (placeholderRef?.current) {
      setPlaceholderRef(placeholderRef);
    }
  }, [setPlaceholderRef, placeholderRef]);

  const dispatch = useDispatch();

  // NB: only when we click on the picture in picture icon
  // close the WTV if we enter in Pip with a match stream
  useEffect(() => {
    if (askPip && isPipCustomActive && type === StreamModes.MATCH) {
      dispatch(setShowWinamaxTv(false));
      setAskPip(false);
    }
  }, [askPip, dispatch, isPipCustomActive, setAskPip, type]);

  useEffect(() => {
    // - [DESKTOP ONLY] selects the WTV button when we go to another route
    // ... and come back to the same match AND native pip is active
    if (!Target.__MOBILE__ && isSameMatchRouteAndPipNativeActive) {
      const floatingVideo = floatingVideoRef.current;

      setActiveItem(tvItemId);

      // NB: we need the remove of z-index for that situation
      // 1 - we activate the native Pip
      // 2 - we selects another item from the match toolbar
      // 3 - we go to another page
      // 4 - we finally go back to the same match
      if (floatingVideo) {
        floatingVideo.style.removeProperty('z-index');
      }
    }

    // - [MOBILE ONLY] deselects tv button when passing on custom Pip
    if (Target.__MOBILE__ && isPipCustomActive) {
      setActiveItem('');
      return;
    }

    // - [DESKTOP ONLY] deselects tv button when not the same route + native pip is not active
    // NB: Desktop only due to the `return` instruction on the previous if
    if (isPipCustomActive && !isSameMatchRouteAndPipNativeActive) {
      setActiveItem('');
    }
  }, [floatingVideoRef, isPipCustomActive, isSameMatchRouteAndPipNativeActive, setActiveItem, tvItemId]);

  // NB: ONLY FOR NATIVE PIP
  // selects the WTV button when we switch to another matchtoolbar item and restore the native PiP
  useEffect(() => {
    if (isPipEnabled && !isPipNativeActive && isMatchRouteRelativeToStream && type === StreamModes.MATCH) {
      setActiveItem(tvItemId);
    }
  }, [setActiveItem, tvItemId, isPipEnabled, isPipNativeActive, isMatchRouteRelativeToStream, type]);

  // NB: ONLY FOR CUSTOM PIP
  // handle custom pip restore when we click on restore picto
  useEffect(() => {
    if (askRestore) {
      setActiveItem(tvItemId);
      setAskRestore(false);
    }
  }, [setActiveItem, setAskRestore, askRestore, tvItemId]);

  // start of stream only for this condition:
  // when the `WTV` matchtoolbar item is not selected and we click on it
  useEffect(() => {
    if (isExpand && askWtv) {
      startStream({ id: matchId, type: StreamModes.MATCH });
      setAskWtv(false);
    }
  }, [askWtv, startStream, matchId, isExpand, setAskWtv]);

  const previousIsTvItemSelected = usePrevious(isTvItemSelected);

  // close stream when changing match toolbar tab with no PiP
  useEffect(() => {
    if (previousIsTvItemSelected && !isTvItemSelected && !isPipCustomActive && !isPipNativeActive) {
      closeStream();
    }
  }, [isTvItemSelected, previousIsTvItemSelected, isPipCustomActive, isPipNativeActive, closeStream]);

  // set a offsetTop value for the placeholder when we are on Sticky mode
  useEffect(() => {
    const stickyOffsetTop = Target.__MOBILE__ ? offsetTop : 0;
    setStickyOffsetTop(isSticky ? stickyOffsetTop : null);
  }, [isSticky, offsetTop, setStickyOffsetTop]);

  return <PiPWrap $isExpand={isExpand || (isTvItemSelected && isSameMatchRouteAndPipNativeActive)} />;
};

export default PiPMatchToolbarController;
